import React, { useEffect, useState } from "react";
import { api } from "@/api";
import { Link } from "react-router-dom";
import { Package, Puzzle, Receipt, Info } from "lucide-react";

const money = (v) => `${Number(v || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

/** Récapitulatif « abonnement + options » — utilisé côté gérant (Facturation) et côté admin (fiche entreprise). */
export function SubscriptionBreakdown({ data, compact = false, legalLink = "/chef/legal?doc=cgv" }) {
  if (!data) return <div className="border border-[#DEE2E6] bg-white p-5 text-xs text-[#868E96]" data-testid="sb-loading">Chargement de l'abonnement…</div>;
  const { plan, options, totals } = data;
  return (
    <div className="border border-[#DEE2E6] bg-white" data-testid="subscription-breakdown">
      <div className="p-5 border-b border-[#DEE2E6] bg-[#F8F9FA] flex items-center justify-between gap-3 flex-wrap">
        <h2 className="font-display text-2xl font-black tracking-tight flex items-center gap-2"><Receipt className="h-5 w-5" />Mon abonnement</h2>
        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 bg-[#0A0A0A] text-white" data-testid="sb-cycle">Cycle {plan.cycle_label}</span>
      </div>
      <div className="divide-y divide-[#DEE2E6]">
        <div className="p-5 flex items-start justify-between gap-4" data-testid="sb-plan">
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#868E96] font-bold">Formule</div>
            <div className="font-display font-black text-lg" data-testid="sb-plan-name">{plan.name || "Formule sur devis"}</div>
            {plan.modules?.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1" data-testid="sb-modules">
                {plan.modules.map((m) => <span key={m.key} className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 border border-[#DEE2E6] bg-[#F8F9FA]"><Puzzle className="h-3 w-3" />{m.name}</span>)}
              </div>
            )}
            {!plan.modules?.length && !compact && <div className="text-xs text-[#868E96] mt-1">Modules inclus dans la formule — définis dans votre offre personnalisée.</div>}
          </div>
          <div className="font-mono font-bold text-right whitespace-nowrap" data-testid="sb-plan-price">{plan.price_label}</div>
        </div>
        {options.length === 0 ? (
          <div className="p-5 text-xs text-[#868E96]" data-testid="sb-options-empty">Aucune option payante — ajoutez-en via PAG Helper (utilisateur supplémentaire, multi-établissements, pointage avancé, packs…).</div>
        ) : options.map((o) => (
          <div key={o.option_key} className="p-5 flex items-start justify-between gap-4" data-testid={`sb-option-${o.option_key}`}>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#868E96] font-bold flex items-center gap-1"><Package className="h-3 w-3" />Option {o.cycle_label}</div>
              <div className="font-semibold text-sm">{o.name}{o.quantity > 1 ? <span className="text-[#868E96]"> × {o.quantity}</span> : null}</div>
              {o.includes?.length > 0 && <div className="text-[11px] text-[#495057] mt-0.5">Inclut : {o.includes.join(", ")}</div>}
            </div>
            <div className="font-mono font-bold text-right whitespace-nowrap">+ {o.total_label}</div>
          </div>
        ))}
      </div>
      <div className="p-5 border-t-2 border-[#0A0A0A] bg-[#F8F9FA]" data-testid="sb-totals">
        <div className="flex flex-wrap justify-between gap-4">
          <div className="text-xs text-[#495057] max-w-md flex items-start gap-2">
            <Info className="h-4 w-4 shrink-0 mt-0.5 text-[#002FA7]" />
            <span>Les options et modules ajoutés à votre demande <b>s'ajoutent au prix de l'abonnement</b> et sont facturés selon leur cycle. Voir les <Link to={legalLink} className="underline hover:text-[#002FA7]" data-testid="sb-cgv-link">CGV, article 3</Link>.</span>
          </div>
          <div className="text-right space-y-1">
            {totals.monthly > 0 && <div data-testid="sb-total-monthly"><span className="text-[10px] uppercase tracking-wider text-[#868E96] font-bold mr-3">Chaque mois</span><span className="font-display font-black text-2xl">{money(totals.monthly)}</span></div>}
            {totals.annual > 0 && <div data-testid="sb-total-annual"><span className="text-[10px] uppercase tracking-wider text-[#868E96] font-bold mr-3">Chaque année</span><span className="font-display font-black text-2xl">{money(totals.annual)}</span></div>}
            {!totals.monthly && !totals.annual && <div className="text-sm text-[#868E96]" data-testid="sb-total-devis">Montant défini sur devis</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

/** Variante admin : charge elle-même le récapitulatif d'une entreprise. */
export function AdminBillingSummary({ entrepriseId, refreshKey }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/admin/entreprises/${entrepriseId}/billing-summary`).then((r) => setData(r.data)).catch(() => setData(null)); }, [entrepriseId, refreshKey]);
  return <SubscriptionBreakdown data={data} compact legalLink="/legal/cgv" />;
}
